const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const K_CONSENT = "nc_consent";
const K_VISITOR = "nc_vid";
const K_SESSION = "nc_sess";
const IDLE_MS = 30 * 60 * 1000;

let queue = [];
let timer = null;
let activeMs = 0;
let lastInteraction = 0;
let started = false;
let session = null;
const dwell = new Map();
const hooks = [];

const rid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
const read = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* storage blocked */ } };
const drop = (k) => { try { window.localStorage.removeItem(k); } catch { /* storage blocked */ } };

export const getConsent = () => read(K_CONSENT);
export const hasConsent = () => read(K_CONSENT) === "granted";

function currentSession() {
  const now = Date.now();
  if (!session) {
    try { session = JSON.parse(read(K_SESSION) || "null"); } catch { session = null; }
  }
  if (!session || now - session.last > IDLE_MS) {
    const existing = read(K_VISITOR);
    const vid = existing || rid();
    if (!existing) write(K_VISITOR, vid);
    session = { id: rid(), vid, returning: !!existing, last: now, done: [], sent: false };
  }
  session.last = now;
  write(K_SESSION, JSON.stringify(session));
  return session;
}

export function getIds() {
  if (!hasConsent()) return { sessionId: null, visitorId: null };
  const s = currentSession();
  return { sessionId: s.id, visitorId: s.vid };
}

export function track(type, name, props = {}) {
  if (!hasConsent()) return;
  currentSession();
  queue.push({ t: Date.now(), type, name: String(name), props });
  if (queue.length >= 30) flush();
  else if (!timer) timer = setTimeout(() => flush(), 4000);
}

export function funnel(key) {
  if (!hasConsent()) return;
  const s = currentSession();
  if (s.done.includes(key)) return;
  s.done.push(key);
  write(K_SESSION, JSON.stringify(s));
  track("funnel", key);
}

export const dwellStart = (key) => {
  const d = dwell.get(key);
  if (!d) dwell.set(key, { since: Date.now(), total: 0 });
  else if (!d.since) d.since = Date.now();
};
export const dwellStop = (key) => {
  const d = dwell.get(key);
  if (d?.since) { d.total += Date.now() - d.since; d.since = 0; }
};

const collectDwell = () => {
  const now = Date.now();
  dwell.forEach((d, key) => {
    const ms = d.total + (d.since ? now - d.since : 0);
    if (d.since) d.since = now;
    d.total = 0;
    if (ms > 400) queue.push({ t: now, type: "section_time", name: key, props: { ms } });
  });
};

export function flush() {
  clearTimeout(timer);
  timer = null;
  if (!hasConsent()) { queue = []; return; }
  hooks.forEach((h) => h());
  collectDwell();
  if (activeMs > 0) { queue.push({ t: Date.now(), type: "active", name: "ms", props: { ms: activeMs } }); activeMs = 0; }
  if (!queue.length) return;
  const s = currentSession();
  const body = JSON.stringify({
    visitorId: s.vid, sessionId: s.id, returning: s.returning, vw: window.innerWidth,
    referrer: s.sent ? "" : document.referrer, landing: window.location.pathname, events: queue.splice(0, 100),
  });
  s.sent = true;
  try {
    const ok = navigator.sendBeacon && navigator.sendBeacon(`${API}/track`, new Blob([body], { type: "text/plain" }));
    if (!ok) fetch(`${API}/track`, { method: "POST", body, headers: { "Content-Type": "text/plain" }, keepalive: true }).catch(() => {});
  } catch { /* analytics must never break the site */ }
  if (queue.length) setTimeout(flush, 500);
}

export function registerFlushHook(fn) {
  hooks.push(fn);
  return () => { const i = hooks.indexOf(fn); if (i >= 0) hooks.splice(i, 1); };
}

export function startAnalytics() {
  if (started || !hasConsent()) return;
  started = true;
  currentSession();
  const touch = () => { lastInteraction = Date.now(); };
  ["mousemove", "scroll", "keydown", "click", "touchstart"].forEach((ev) => window.addEventListener(ev, touch, { passive: true }));
  touch();
  setInterval(() => { if (document.visibilityState === "visible" && Date.now() - lastInteraction < 10000) activeMs += 1000; }, 1000);
  setInterval(flush, 15000);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
  window.addEventListener("pagehide", flush);
}

export function setConsent(value) {
  write(K_CONSENT, value);
  if (value === "granted") startAnalytics();
  else { queue = []; drop(K_VISITOR); drop(K_SESSION); session = null; }
}

export function resetConsent() {
  queue = []; drop(K_CONSENT); drop(K_VISITOR); drop(K_SESSION); session = null;
}
